import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Chip,
    LinearProgress,
    Tabs,
    Tab,
    Breadcrumbs,
    Link as MuiLink,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Avatar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    InputAdornment,
    Tooltip,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ContentCopy as CopyIcon,
    Image as ImageIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Add as AddIcon,
    Upload as UploadIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';
import { ClientRoleManagement } from '../components/ClientRoleManagement';
import { IClient, ICreateClientDto } from "@tintoa/identity-service-client";

interface Client extends IClient {
    icon?: { data: string; contentType: string; uploadedAt: Date | string };
}

interface AppUser {
    id?: string;
    _id?: string;
    tid: string;
    clientId: string | { toString(): string };
    clientApp?: {
        name: string;
    };
    scopes: string[];
    roles?: Array<{
        id: string;
        name: string;
        ident: string;
    }>;
    meta: Record<string, string>;
    profile?: {
        firstname: string;
        lastname: string;
        email?: string;
    };
}

function getAppUserId(au: AppUser): string {
    return au._id ?? au.id ?? '';
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`client-tabpanel-${index}`}
            aria-labelledby={`client-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export const ClientDetailPage: FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [client, setClient] = useState<Client | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState<ICreateClientDto>({
        name: '',
        description: '',
        authCallbackUrl: '',
        url: '',
        requiredRoleIds: [],
        isPrivate: false
    });
    const [errors, setErrors] = useState<Partial<ICreateClientDto>>({});
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [removeIcon, setRemoveIcon] = useState(false);
    const [iconError, setIconError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [availableRoles, setAvailableRoles] = useState<Array<{id: string; name: string; ident: string}>>([]);
    const [appUsers, setAppUsers] = useState<AppUser[]>([]);
    const [totalAppUsers, setTotalAppUsers] = useState(0);
    const [appUserPage, setAppUserPage] = useState(0);
    const [appUserRowsPerPage, setAppUserRowsPerPage] = useState(10);
    const [loadingAppUsers, setLoadingAppUsers] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteAppUserDialogOpen, setDeleteAppUserDialogOpen] = useState(false);
    const [appUserToDelete, setAppUserToDelete] = useState<AppUser | null>(null);
    const [apiKeys, setApiKeys] = useState<Array<{name: string; value: string}>>([]);
    const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
    const [newApiKeyName, setNewApiKeyName] = useState('');
    const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);

    useEffect(() => {
        if (clientId) {
            loadClient();
            loadAvailableRoles();
        }
    }, [clientId]);

    useEffect(() => {
        if (activeTab === 2 && clientId) {
            loadApiKeys();
        }
        if (activeTab === 3 && clientId) {
            loadAppUsers();
        }
    }, [activeTab, clientId, appUserPage, appUserRowsPerPage]);

    const loadClient = async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            const data = await adminApiClient.getClientById(clientId);
            const transformedData: Client = {
                ...data,
                secret: data.secret || '',
                apiKeys: data.apiKeys.map(apiKey => ({
                    name: apiKey.name,
                    value: apiKey.value || ''
                })),
                requiredRoleIds: data.requiredRoleIds ? data.requiredRoleIds.map((id: any) => id.toString()) : []
            };
            setClient(transformedData);
            setFormData({
                name: data.name,
                description: data.description,
                authCallbackUrl: data.authCallbackUrl || '',
                url: data.url || '',
                requiredRoleIds: data.requiredRoleIds ? data.requiredRoleIds.map((id: any) => id.toString()) : [],
                isPrivate: data.isPrivate ?? false
            });
            setApiKeys(transformedData.apiKeys);
        } catch (error: any) {
            console.error('Failed to load client:', error);
            enqueueSnackbar('Fehler beim Laden des Clients', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableRoles = async () => {
        try {
            const roles = await adminApiClient.getRoles();
            setAvailableRoles(roles);
        } catch (error) {
            console.error('Error loading available roles:', error);
        }
    };

    const loadAppUsers = async () => {
        if (!clientId) return;
        try {
            setLoadingAppUsers(true);
            const result = await adminApiClient.getClientAppUsers(clientId, {
                limit: appUserRowsPerPage,
                offset: appUserPage * appUserRowsPerPage
            });
            setAppUsers(result.data);
            setTotalAppUsers(result.total);
        } catch (error: any) {
            console.error('Failed to load app users:', error);
            enqueueSnackbar('Fehler beim Laden der App-Benutzer', { variant: 'error' });
        } finally {
            setLoadingAppUsers(false);
        }
    };

    const loadApiKeys = () => {
        if (client) {
            setApiKeys(client.apiKeys);
        }
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const validateIconFile = (file: File): boolean => {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setIconError('Nur JPEG, PNG oder SVG erlaubt.');
            return false;
        }
        if (file.size > 100 * 1024) {
            setIconError('Datei darf maximal 100KB groß sein.');
            return false;
        }
        setIconError(null);
        return true;
    };

    const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (validateIconFile(file)) {
            setIconFile(file);
            setRemoveIcon(false);
        }
    };

    const extractFQDN = (url: string): string => {
        try {
            const urlObj = new URL(url);
            return `${urlObj.protocol}//${urlObj.host}`;
        } catch {
            return '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<ICreateClientDto> = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name ist erforderlich';
        }

        const authCallbackUrl = formData.authCallbackUrl?.trim() ?? '';
        if (authCallbackUrl) {
            try {
                new URL(authCallbackUrl);
            } catch {
                newErrors.authCallbackUrl = 'Auth Callback URL ist ungültig';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        if (iconFile && !validateIconFile(iconFile)) return;

        setSubmitting(true);
        try {
            const submitData: ICreateClientDto = {
                name: formData.name,
                authCallbackUrl: formData.authCallbackUrl?.trim() ?? '',
                requiredRoleIds: formData.requiredRoleIds || []
            };

            if (formData.description && formData.description.trim()) {
                submitData.description = formData.description;
            }
            if (formData.url && formData.url.trim()) {
                submitData.url = formData.url;
            }
            if (formData.isPrivate !== undefined) {
                submitData.isPrivate = formData.isPrivate;
            }

            await adminApiClient.updateClient(clientId!, submitData);

            if (iconFile) {
                try {
                    await adminApiClient.uploadClientIcon(clientId!, iconFile);
                    enqueueSnackbar('Icon erfolgreich hochgeladen', { variant: 'success' });
                } catch (iconErr) {
                    console.error('Failed to upload icon:', iconErr);
                    enqueueSnackbar('Fehler beim Hochladen des Icons', { variant: 'error' });
                }
            } else if (removeIcon && client?.icon) {
                try {
                    await adminApiClient.deleteClientIcon(clientId!);
                    enqueueSnackbar('Icon entfernt', { variant: 'success' });
                } catch (iconErr) {
                    console.error('Failed to delete icon:', iconErr);
                    enqueueSnackbar('Fehler beim Entfernen des Icons', { variant: 'error' });
                }
            }

            enqueueSnackbar('Client erfolgreich aktualisiert', { variant: 'success' });
            loadClient();
        } catch (error) {
            console.error('Failed to save client:', error);
            enqueueSnackbar('Fehler beim Speichern des Clients', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar(`${label} in die Zwischenablage kopiert`, { variant: 'success' });
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            enqueueSnackbar('Fehler beim Kopieren in die Zwischenablage', { variant: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!clientId) return;
        try {
            await adminApiClient.deleteClient(clientId);
            enqueueSnackbar('Client erfolgreich gelöscht', { variant: 'success' });
            navigate('/app/admin/clients');
        } catch (error) {
            console.error('Failed to delete client:', error);
            enqueueSnackbar('Fehler beim Löschen des Clients', { variant: 'error' });
        }
    };

    const handleCreateApiKey = async () => {
        if (!clientId || !newApiKeyName.trim()) return;
        try {
            const result = await adminApiClient.addApiKey(clientId, { name: newApiKeyName.trim() });
            enqueueSnackbar('API-Key erfolgreich erstellt', { variant: 'success' });
            if (result && result.value) {
                enqueueSnackbar(`API-Key erstellt: ${result.value}`, {
                    variant: 'info',
                    autoHideDuration: 10000,
                    action: (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => navigator.clipboard.writeText(result.value || '')}
                        >
                            Kopieren
                        </Button>
                    )
                });
            }
            setApiKeyDialogOpen(false);
            setNewApiKeyName('');
            loadClient();
        } catch (error: any) {
            console.error('Failed to create API key:', error);
            enqueueSnackbar(`Fehler beim Erstellen des API-Keys: ${error.message || 'Unbekannter Fehler'}`, { variant: 'error' });
        }
    };

    const handleDeleteApiKey = async (apiKeyName: string) => {
        if (!clientId) return;
        if (window.confirm(`Möchten Sie den API-Key "${apiKeyName}" wirklich löschen?`)) {
            try {
                await adminApiClient.removeApiKey(clientId, apiKeyName);
                enqueueSnackbar('API-Key erfolgreich gelöscht', { variant: 'success' });
                loadClient();
            } catch (error: any) {
                console.error('Failed to delete API key:', error);
                enqueueSnackbar(`Fehler beim Löschen des API-Keys: ${error.message || 'Unbekannter Fehler'}`, { variant: 'error' });
            }
        }
    };

    const toggleKeyVisibility = (keyName: string) => {
        const newVisibleKeys = new Set(visibleKeys);
        if (newVisibleKeys.has(keyName)) {
            newVisibleKeys.delete(keyName);
        } else {
            newVisibleKeys.add(keyName);
        }
        setVisibleKeys(newVisibleKeys);
    };

    const copyApiKeyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar('In Zwischenablage kopiert', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Fehler beim Kopieren', { variant: 'error' });
        }
    };

    const maskApiKey = (apiKey: string) => {
        if (apiKey.length <= 12) return '••••••••••••';
        return apiKey.substring(0, 8) + '••••••••••••' + apiKey.substring(apiKey.length - 4);
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    if (!client) {
        return (
            <Box>
                <Typography variant="h6" color="error">
                    Client nicht gefunden
                </Typography>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/app/admin/clients')}
                    sx={{ mt: 2 }}
                >
                    Zurück zur Clients-Liste
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <MuiLink
                        component={Link}
                        to="/app/admin/clients"
                        sx={{ textDecoration: 'none' }}
                    >
                        Clients
                    </MuiLink>
                    <Typography color="text.primary">
                        {client.name}
                    </Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate('/app/admin/clients')}
                            sx={{ mr: 1 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                {client.name}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {client.description}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Tabs */}
            <Paper>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="client detail tabs">
                    <Tab label="Allgemein" />
                    <Tab label="Rollen" />
                    <Tab label="Api-Keys" />
                    <Tab label="AppUser" />
                    <Tab label="Löschen" />
                </Tabs>

                {/* Tab 1: Allgemein */}
                <TabPanel value={activeTab} index={0}>
                    <Box>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                Client-Informationen
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Bearbeiten Sie die Client-Konfiguration
                            </Typography>
                        </Box>

                        {/* Icon and Basic Info Section */}
                        <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: { xs: '1fr', lg: 'auto 1fr' },
                            gap: 4,
                            mb: 4,
                            alignItems: 'flex-start'
                        }}>
                            {/* Icon Section */}
                            <Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                    {(client.icon && !removeIcon && !iconFile) ? (
                                        <Avatar
                                            src={`data:${client.icon.contentType};base64,${client.icon.data}`}
                                            sx={{ width: 120, height: 120 }}
                                        />
                                    ) : iconFile ? (
                                        <Avatar
                                            src={URL.createObjectURL(iconFile)}
                                            sx={{ width: 120, height: 120 }}
                                        />
                                    ) : (
                                        <Avatar sx={{ width: 120, height: 120, bgcolor: 'action.hover' }}>
                                            <ImageIcon sx={{ fontSize: 48 }} color="disabled" />
                                        </Avatar>
                                    )}
                                    
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            component="label"
                                            size="small"
                                            disabled={submitting}
                                            color="primary"
                                            sx={{ border: 1, borderColor: 'divider' }}
                                        >
                                            <UploadIcon fontSize="small" />
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                                                onChange={handleIconFileChange}
                                            />
                                        </IconButton>
                                        {client.icon && !iconFile && !removeIcon && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setRemoveIcon(true)}
                                                disabled={submitting}
                                                sx={{ border: 1, borderColor: 'divider' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                        {removeIcon && (
                                            <IconButton
                                                size="small"
                                                onClick={() => setRemoveIcon(false)}
                                                disabled={submitting}
                                                sx={{ border: 1, borderColor: 'divider' }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                    
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Icon (optional)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Max. 100KB, JPEG, PNG oder SVG
                                        </Typography>
                                        {iconError && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                                {iconError}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Name, URL, Beschreibung */}
                            <Box>
                                <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: 3,
                                    mb: 3
                                }}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                    />
                                    <TextField
                                        fullWidth
                                        label="URL (optional)"
                                        value={formData.url || ''}
                                        onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                        helperText="Haupt-URL der Anwendung"
                                    />
                                </Box>

                                <TextField
                                    fullWidth
                                    label="Beschreibung"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    error={!!errors.description}
                                    helperText={errors.description}
                                    multiline
                                    rows={3}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isPrivate ?? false}
                                        onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                                    />
                                }
                                label="Privat"
                            />
                            <Typography variant="caption" color="text.secondary">
                                Wenn aktiviert, bekommen Benutzer keine Vorschläge für diese Client-Anwendung.
                            </Typography>
                        </Box>

                        <TextField
                            fullWidth
                            label="Auth Callback URL (optional)"
                            value={formData.authCallbackUrl ?? ''}
                            onChange={(e) => {
                                const callbackUrl = e.target.value;
                                const fqdn = extractFQDN(callbackUrl);
                                setFormData({ 
                                    ...formData, 
                                    authCallbackUrl: callbackUrl,
                                    url: fqdn || formData.url
                                });
                            }}
                            error={!!errors.authCallbackUrl}
                            helperText={errors.authCallbackUrl || 'Ohne Callback-URL ist OAuth-Login nicht möglich'}
                            sx={{ mb: 3 }}
                        />
                        
                        <FormControl fullWidth sx={{ mb: 1 }}>
                            <InputLabel id="required-roles-label">Erforderliche Rollen (optional)</InputLabel>
                            <Select
                                labelId="required-roles-label"
                                multiple
                                value={formData.requiredRoleIds || []}
                                onChange={(e: any) => setFormData({ ...formData, requiredRoleIds: e.target.value as string[] })}
                                input={<OutlinedInput label="Erforderliche Rollen (optional)" />}
                                renderValue={(selected: any) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value: any) => {
                                            const role = availableRoles.find(r => r.id === value);
                                            return (
                                                <Chip 
                                                    key={value} 
                                                    label={role?.name || value} 
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {availableRoles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Typography variant="body2" fontWeight="medium">
                                                {role.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {role.ident}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 4, display: 'block' }}>
                            Systemrollen am Account. Der Benutzer muss mindestens eine davon besitzen, um sich bei dieser Anwendung anmelden zu können.
                        </Typography>

                        {/* Client Credentials in Grid */}
                        <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="h6" gutterBottom>
                                Client-Anmeldedaten
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Diese Werte werden für die Authentifizierung benötigt
                            </Typography>
                            
                            <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 3
                            }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Client ID
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            value={client.id}
                                            InputProps={{
                                                readOnly: true,
                                                sx: { fontFamily: 'monospace' }
                                            }}
                                            size="small"
                                        />
                                        <IconButton
                                            onClick={() => handleCopyToClipboard(client.id, 'Client ID')}
                                            title="Client ID kopieren"
                                        >
                                            <CopyIcon />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Client Secret
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            value={client.secret}
                                            InputProps={{
                                                readOnly: true,
                                                sx: { fontFamily: 'monospace' }
                                            }}
                                            size="small"
                                        />
                                        <IconButton
                                            onClick={() => handleCopyToClipboard(client.secret, 'Client Secret')}
                                            title="Client Secret kopieren"
                                        >
                                            <CopyIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Wird gespeichert...' : 'Speichern'}
                            </Button>
                        </Box>
                    </Box>
                </TabPanel>

                {/* Tab 2: Rollen */}
                <TabPanel value={activeTab} index={1}>
                    {clientId && (
                        <ClientRoleManagement
                            clientId={clientId}
                            clientName={client.name}
                        />
                    )}
                </TabPanel>

                {/* Tab 3: Api-Keys */}
                <TabPanel value={activeTab} index={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                API-Keys
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Verwalten Sie API-Keys für diese Client-Anwendung
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setApiKeyDialogOpen(true)}
                            size="small"
                        >
                            Neuer API-Key
                        </Button>
                    </Box>

                    {apiKeys.length === 0 ? (
                        <Alert severity="info">
                            Keine API-Keys für diesen Client vorhanden.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="subtle">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>API-Key</TableCell>
                                        <TableCell align="right" sx={{ width: 100 }}>Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {apiKeys.map((apiKey) => {
                                        const isVisible = visibleKeys.has(apiKey.name);
                                        return (
                                            <TableRow key={apiKey.name} hover>
                                                <TableCell>
                                                    <Typography variant="subtitle2" fontWeight="medium">
                                                        {apiKey.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            value={isVisible ? apiKey.value : maskApiKey(apiKey.value)}
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            InputProps={{
                                                                readOnly: true,
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <Tooltip title={isVisible ? 'Verstecken' : 'Anzeigen'}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => toggleKeyVisibility(apiKey.name)}
                                                                            >
                                                                                {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Kopieren">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => copyApiKeyToClipboard(apiKey.value)}
                                                                            >
                                                                                <CopyIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </InputAdornment>
                                                                ),
                                                                sx: { fontFamily: 'monospace' }
                                                            }}
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteApiKey(apiKey.name)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Create API Key Dialog */}
                    <Dialog
                        open={apiKeyDialogOpen}
                        onClose={() => setApiKeyDialogOpen(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>
                            Neuen API-Key erstellen
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ pt: 2 }}>
                                <TextField
                                    fullWidth
                                    label="API-Key Name"
                                    value={newApiKeyName}
                                    onChange={(e) => setNewApiKeyName(e.target.value)}
                                    placeholder="z.B. production-key, staging-key"
                                    helperText="Eindeutiger Name für diesen API-Key"
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setApiKeyDialogOpen(false)}>Abbrechen</Button>
                            <Button onClick={handleCreateApiKey} variant="contained" disabled={!newApiKeyName.trim()}>
                                API-Key erstellen
                            </Button>
                        </DialogActions>
                    </Dialog>
                </TabPanel>

                {/* Tab 4: AppUser */}
                <TabPanel value={activeTab} index={3}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            App-Benutzer
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Alle App-Benutzer dieser Client-Anwendung
                        </Typography>
                    </Box>

                    {loadingAppUsers ? (
                        <Box sx={{ p: 2 }}>
                            <LinearProgress />
                        </Box>
                    ) : appUsers.length === 0 ? (
                        <Alert severity="info">
                            Keine App-Benutzer für diesen Client vorhanden.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="subtle">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Benutzer</TableCell>
                                        <TableCell>Rollen</TableCell>
                                        <TableCell>Scopes</TableCell>
                                        <TableCell>Metadaten</TableCell>
                                        <TableCell align="right" sx={{ width: 120 }}>Aktionen</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {appUsers.map((appUser) => (
                                        <TableRow key={getAppUserId(appUser)} hover>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {[appUser.profile?.firstname, appUser.profile?.lastname].filter(Boolean).join(' ') || appUser.tid || 'AppUser'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {appUser.profile?.email || appUser.tid}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {appUser.roles && appUser.roles.length > 0 ? (
                                                        appUser.roles.map((role) => (
                                                            <Chip
                                                                key={role.id}
                                                                label={role.name}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Keine Rollen
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {appUser.scopes && appUser.scopes.length > 0 ? (
                                                        appUser.scopes.map((scope) => (
                                                            <Chip
                                                                key={scope}
                                                                label={scope}
                                                                size="small"
                                                                color="secondary"
                                                                variant="filled"
                                                            />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Keine Scopes
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {Object.keys(appUser.meta || {}).length} Einträge
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => navigate(`/app/admin/clients/${clientId}/app-users/${getAppUserId(appUser)}`)}
                                                >
                                                    Bearbeiten
                                                </Button>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    title="App-Benutzer löschen"
                                                    onClick={() => {
                                                        setAppUserToDelete(appUser);
                                                        setDeleteAppUserDialogOpen(true);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={totalAppUsers}
                                page={appUserPage}
                                onPageChange={(_e, newPage) => setAppUserPage(newPage)}
                                rowsPerPage={appUserRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setAppUserRowsPerPage(parseInt(e.target.value, 10));
                                    setAppUserPage(0);
                                }}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                labelRowsPerPage="Zeilen pro Seite"
                            />
                        </TableContainer>
                    )}

                    <Dialog
                        open={deleteAppUserDialogOpen}
                        onClose={() => {
                            setDeleteAppUserDialogOpen(false);
                            setAppUserToDelete(null);
                        }}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>App-Benutzer löschen</DialogTitle>
                        <DialogContent>
                            <Typography>
                                {appUserToDelete
                                    ? `App-Benutzer von „${appUserToDelete.profile ? `${appUserToDelete.profile.firstname} ${appUserToDelete.profile.lastname}` : appUserToDelete.tid}“ wirklich löschen?`
                                    : ''}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setDeleteAppUserDialogOpen(false);
                                    setAppUserToDelete(null);
                                }}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                color="error"
                                variant="contained"
                                onClick={async () => {
                                    if (!appUserToDelete) return;
                                    try {
                                        const appUserIdToDelete = getAppUserId(appUserToDelete);
                                        if (!appUserIdToDelete) {
                                            enqueueSnackbar('App-Benutzer-ID fehlt', { variant: 'error' });
                                            return;
                                        }
                                        await adminApiClient.deleteAppUser(appUserToDelete.tid, appUserIdToDelete);
                                        setDeleteAppUserDialogOpen(false);
                                        setAppUserToDelete(null);
                                        await loadAppUsers();
                                        enqueueSnackbar('App-Benutzer erfolgreich gelöscht', { variant: 'success' });
                                    } catch (error: unknown) {
                                        console.error('Failed to delete app user:', error);
                                        enqueueSnackbar('Fehler beim Löschen des App-Benutzers', { variant: 'error' });
                                    }
                                }}
                            >
                                Löschen
                            </Button>
                        </DialogActions>
                    </Dialog>
                </TabPanel>

                {/* Tab 5: Löschen */}
                <TabPanel value={activeTab} index={4}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                            Warnung: Client löschen
                        </Typography>
                        <Typography variant="body2">
                            Die App funktioniert nicht mehr und alle Daten gehen verloren, wenn Sie sie löschen.
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setActiveTab(0)}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Client löschen
                        </Button>
                    </Box>

                    {/* Delete Confirmation Dialog */}
                    <Dialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                    >
                        <DialogTitle>Client löschen</DialogTitle>
                        <DialogContent>
                            <Typography>
                                Möchten Sie den Client "{client.name}" wirklich löschen?
                                Diese Aktion kann nicht rückgängig gemacht werden.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
                            <Button onClick={handleDelete} color="error" variant="contained">
                                Löschen
                            </Button>
                        </DialogActions>
                    </Dialog>
                </TabPanel>
            </Paper>
        </Box>
    );
};
